import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../api";
import { Mail, Play, RefreshCw, Eye, Send, ChevronLeft, ExternalLink, UserCheck, Sparkles, Download, Trash2, Wand2, X, Star, MapPin, Pencil, Save, Search } from "lucide-react";

const fmt = (n) => (n == null ? "—" : new Intl.NumberFormat("it-IT").format(n));

const StatusBadge = ({ status }) => {
  const map = {
    pending: ["badge-gray", "In attesa"],
    screened: ["badge-orange", "Pre-screen"],
    selected: ["badge-orange", "Selezionata"],
    analyzed: ["badge-green", "Analizzata"],
    email_sent: ["badge-brand", "Email inviata"],
    converted: ["badge-green", "Convertita"],
  };
  const [cls, label] = map[status] || ["badge-gray", status];
  return <span className={`badge ${cls}`}>{label}</span>;
};

const TrafficLight = ({ v }) => {
  if (!v) return <span className="text-muted">—</span>;
  if (v === "no_website") return <span className="badge badge-gray">no sito</span>;
  if (v === "target") return <span className="badge badge-green">🎯 Target ideale</span>;
  if (v === "review") return <span className="badge badge-orange">⚠️ Da valutare</span>;
  if (v === "skip") return <span className="badge badge-red">⛔ Skip</span>;
  if (v === "green") return <span className="badge badge-green">verde</span>;
  if (v === "orange") return <span className="badge badge-orange">arancio</span>;
  if (v === "red") return <span className="badge badge-red">rosso</span>;
  return <span className="badge badge-gray">{v}</span>;
};

export default function ListaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewModal, setPreviewModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [l, i] = await Promise.all([
      api.get(`/lists/${id}`),
      api.get(`/lists/${id}/items`),
    ]);
    setList(l.data);
    setItems(i.data);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    // Poll while there are running/queued analyses
    const hasActive = items.some(it => it.analysis && ["queued","running"].includes(it.analysis.status));
    if (!hasActive) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [items, reload]);

  const selectedIds = Object.keys(selected).filter(k => selected[k]);
  const toggle = (id) => setSelected(s => ({...s, [id]: !s[id]}));

  const prescreen = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try { await api.post(`/lists/${id}/items/prescreen`, { itemIds: selectedIds }); await reload(); setSelected({}); }
    finally { setBusy(false); }
  };

  const analyze = async () => {
    if (selectedIds.length === 0) return;
    setBusy(true);
    try { await api.post(`/lists/${id}/items/analyze-bulk`, { itemIds: selectedIds }); await reload(); setSelected({}); }
    finally { setBusy(false); }
  };

  const retryOne = async (item) => {
    setBusy(true);
    try { await api.post(`/lists/${id}/items/${item.id}/analyze`); await reload(); }
    finally { setBusy(false); }
  };

  const enrichOwner = async (item) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/companies/${item.companyId}/enrich-stakeholders`);
      if (data.success) await reload();
      else alert("Nessun titolare trovato: " + (data.error || ""));
    } finally { setBusy(false); }
  };

  const removeFromList = async (item) => {
    const c = item.company || {};
    if (!window.confirm(
      `Rimuovere "${c.companyName}" da questa lista?\n\nL'azienda resterà nel CRM e potrà essere aggiunta ad altre liste.`
    )) return;
    setBusy(true);
    try {
      await api.delete(`/lists/${id}/items/${item.id}`);
      await reload();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const removeManyFromList = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) return;
    if (!window.confirm(`Rimuovere ${ids.length} aziende da questa lista?\n\nResteranno comunque nel CRM.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lists/${id}/items/bulk-delete`, { itemIds: ids });
      setSelected({});
      await reload();
      alert(`✅ Rimosse ${data.deleted} aziende dalla lista.\n` +
            `Cascade: ${data.cascaded_analyses} analisi + ${data.cascaded_emails} email eliminate.`);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  // Bulk-remove by verdict (one server call instead of N delete loops).
  // `lights` is an array of trafficLight values ("skip", "review", "no_website", "target").
  const removeByVerdict = async (lights, friendlyLabel) => {
    const count = items.filter(it => lights.includes(it.trafficLight)).length;
    if (count === 0) {
      alert(`Nessuna azienda con verdetto ${friendlyLabel} in questa lista.`);
      return;
    }
    if (!window.confirm(
      `Rimuovere ${count} aziende con verdetto "${friendlyLabel}" da questa lista?\n\n` +
      `Resteranno comunque nel CRM. Le analisi e le email correlate verranno eliminate.`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/lists/${id}/items/bulk-delete`, { trafficLightIn: lights });
      setSelected({});
      await reload();
      alert(`✅ Rimosse ${data.deleted} aziende.\n` +
            `Cascade: ${data.cascaded_analyses} analisi + ${data.cascaded_emails} email eliminate.`);
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  // Select all items with one of the given trafficLight values (does NOT
  // deselect others — user can stack selections like SKIP + REVIEW).
  const selectByVerdict = (lights) => {
    const matchIds = items.filter(it => lights.includes(it.trafficLight)).map(it => it.id);
    if (matchIds.length === 0) return;
    setSelected(s => {
      const next = { ...s };
      matchIds.forEach(id => { next[id] = true; });
      return next;
    });
  };

  const openPreview = async (item) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/lists/${id}/items/${item.id}/email/preview`);
      setPreviewModal({ ...data, item });
    } catch (e) {
      alert(e.response?.data?.detail || "Errore anteprima");
    } finally { setLoading(false); }
  };

  const [aiEmailModal, setAiEmailModal] = useState(null);
  const [editModal, setEditModal] = useState(null); // { item, form }
  const [zbProgress, setZbProgress] = useState(null); // { source, total, done, ... }
  const [searchQuery, setSearchQuery] = useState("");
  const [verdictFilter, setVerdictFilter] = useState("all"); // "all" | "target" | "review" | "skip" | "no_website" | "none"
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  // Counts by verdict — drive the toolbar pills and badges.
  const verdictCounts = React.useMemo(() => {
    const c = { target: 0, review: 0, skip: 0, no_website: 0, none: 0 };
    items.forEach(it => {
      const v = it.trafficLight;
      if (v === "target") c.target++;
      else if (v === "review") c.review++;
      else if (v === "skip") c.skip++;
      else if (v === "no_website") c.no_website++;
      else c.none++;
    });
    return c;
  }, [items]);

  // Filtered items based on the search query (matches name, website, email, city)
  // AND on the active verdict filter (target/review/skip/none).
  const filteredItems = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return items.filter(it => {
      // Verdict filter first (cheaper)
      if (verdictFilter !== "all") {
        const v = it.trafficLight || "none";
        if (verdictFilter === "none" ? (v && v !== "none") : v !== verdictFilter) return false;
      }
      if (!q) return true;
      const c = it.company || {};
      return (
        (c.companyName || "").toLowerCase().includes(q) ||
        (c.website || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.pec || "").toLowerCase().includes(q) ||
        (c.city || "").toLowerCase().includes(q) ||
        (c.vatCode || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q)
      );
    });
  }, [items, searchQuery, verdictFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pagedItems = React.useMemo(
    () => filteredItems.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredItems, safePage]
  );

  // "Select all" toggles only the items currently visible (i.e. after the
  // verdict filter + search). Without this, filtering by "No sito" and
  // clicking the header checkbox would silently grab the full 677-row list.
  const visibleIds = React.useMemo(() => filteredItems.map(it => it.id), [filteredItems]);
  const allSel = visibleIds.length > 0 && visibleIds.every(id => selected[id]);
  const toggleAll = () => {
    if (allSel) {
      // Deselect only the currently-visible rows; keep selections made on
      // other filters (so the user can stack selections across filter views).
      setSelected(s => {
        const next = { ...s };
        visibleIds.forEach(id => { delete next[id]; });
        return next;
      });
    } else {
      setSelected(s => {
        const next = { ...s };
        visibleIds.forEach(id => { next[id] = true; });
        return next;
      });
    }
  };


  // Reset to page 1 whenever the search query or the verdict filter changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery, verdictFilter]);

  const verifyAllEmails = async () => {
    if (!window.confirm(
      `Avviare la verifica ZeroBounce di tutte le ${items.length} email della lista?\n\n` +
      `Le email già verificate negli ultimi 60 giorni vengono saltate (cache).\n` +
      `Costo stimato: ~€${(items.length * 0.007).toFixed(2)}.\n\n` +
      `La verifica gira in background — puoi continuare a usare l'app.`
    )) return;
    try {
      const { data } = await api.post(`/email/verify/list/${id}`);
      if (!data.source) {
        alert(data.message || "Nessuna email da verificare.");
        return;
      }
      setZbProgress({ source: data.source, total: data.queued, done: 0, status: "running" });
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    }
  };

  // Poll ZeroBounce progress while a bulk verification is running.
  useEffect(() => {
    if (!zbProgress || zbProgress.status === "completed" || zbProgress.status === "failed") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/email/verify/bulk-status?source=${encodeURIComponent(zbProgress.source)}`);
        setZbProgress(data);
        if (data.status === "completed" || data.status === "failed") {
          // refresh the list to surface the new emailVerifiedStatus on each row
          reload();
        }
      } catch (e) { /* ignore transient */ }
    }, 3000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zbProgress?.source, zbProgress?.status]);

  const openEdit = (item) => {
    const c = item.company || {};
    setEditModal({
      item,
      form: {
        website: c.website || "",
        email: c.email || "",
        phone: c.phone || "",
        pec: c.pec || "",
      },
    });
  };

  const saveEdit = async () => {
    if (!editModal) return;
    setBusy(true);
    try {
      await api.patch(`/crm/companies/${editModal.item.companyId}`, editModal.form);
      setEditModal(null);
      await reload();
    } catch (e) {
      alert("Errore salvataggio: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const generateAiEmail = async (item, force = false) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/ai-email/generate/${item.id}`, { force });
      setAiEmailModal({ ...data, item });
    } catch (e) {
      alert("Errore generazione AI: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const generateAiBulk = async () => {
    if (selectedIds.length === 0) {
      alert("Seleziona almeno un lead per la generazione bulk.");
      return;
    }
    if (!window.confirm(
      `Generare email AI personalizzate per ${selectedIds.length} lead?\n\n` +
      `Costo stimato: ~€${(selectedIds.length * 0.011).toFixed(2)} (Claude Sonnet 4.5).\n` +
      `Le email già generate sulla stessa analisi useranno la cache (zero costo).`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai-email/generate-bulk", {
        list_item_ids: selectedIds,
      });
      alert(`Generazione completata!\n\n` +
            `✓ ${data.generated} nuove\n` +
            `♻ ${data.cached} dalla cache\n` +
            `${data.skipped > 0 ? `⚠ ${data.skipped} senza analisi completata\n` : ""}` +
            `${data.errors > 0 ? `✗ ${data.errors} errori` : ""}`);
    } finally { setBusy(false); }
  };

  // v2.59 — on a quality warning (409) show the reasons and let Max decide.
  const postSend = async (itemId, extra = {}) => {
    try {
      return await api.post(`/lists/${id}/items/${itemId}/email/send`, extra);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "quality_check") {
        const ok = window.confirm(
          `⚠ Dati sospetti in questo report:\n\n· ${d.reasons.join("\n· ")}\n\n` +
          `Vuoi inviarlo comunque?`);
        if (!ok) return null;
        return await api.post(`/lists/${id}/items/${itemId}/email/send`,
                              { ...extra, confirmQuality: true });
      }
      throw e;
    }
  };

  const sendEmail = async (item) => {
    if (!window.confirm(`Inviare l'email a ${item.company?.email || item.company?.pec}?`)) return;
    setBusy(true);
    try {
      const res = await postSend(item.id);
      if (!res) return;
      const { data } = res;
      if (data.success) { alert("Email inviata!"); setPreviewModal(null); await reload(); }
      else alert("Errore: " + (data.error || "sconosciuto"));
    } finally { setBusy(false); }
  };

  const sendEmailDirect = async (item) => {
    const c = item.company || {};
    const to = c.email || c.pec;
    if (!to) {
      alert("Manca l'email del destinatario.");
      return;
    }
    if (!window.confirm(
      `📧 Invio diretto (senza anteprima)\n\n` +
      `Destinatario: ${to}\n` +
      `Azienda: ${c.companyName}\n\n` +
      `Userà il template T1 della lista + PDF dell'analisi.\n` +
      `Confermi l'invio?`
    )) return;
    setBusy(true);
    try {
      const res = await postSend(item.id);
      if (!res) return;
      const { data } = res;
      if (data.success) { alert("Email inviata!"); await reload(); }
      else alert("Errore: " + (data.error || "sconosciuto"));
    } finally { setBusy(false); }
  };

  const [sendModal, setSendModal] = useState(null); // { preview }
  const [sendFilters, setSendFilters] = useState({
    onlyGreenTarget: false,
    requireOwner: false,
    notContactedDays: 30,
    notContactedEnabled: false,
    includePec: false,
    requireAnalysis: true,
    useAiEmails: false,
    verifyFilter: "off",  // "off" | "exclude_bad" | "only_valid"
    verifyMinScore: 80,
    requireVerified: false,
  });

  const buildFilters = () => {
    const targetIds = selectedIds.length > 0 ? selectedIds : null;
    return {
      itemIds: targetIds,
      includePec: sendFilters.includePec,
      requireAnalysis: sendFilters.requireAnalysis,
      onlyGreenTarget: sendFilters.onlyGreenTarget,
      requireOwner: sendFilters.requireOwner,
      notContactedDays: sendFilters.notContactedEnabled ? sendFilters.notContactedDays : null,
      useAiEmails: sendFilters.useAiEmails,
      verifyFilter: sendFilters.verifyFilter,
      verifyMinScore: sendFilters.verifyFilter !== "off" ? sendFilters.verifyMinScore : 0,
      requireVerified: sendFilters.requireVerified,
    };
  };

  const openSendModal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lists/${id}/email/send-bulk-preview`, buildFilters());
      setSendModal({ preview: data });
    } catch (e) {
      alert("Errore preview: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const refreshPreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lists/${id}/email/send-bulk-preview`, buildFilters());
      setSendModal({ preview: data });
    } finally { setBusy(false); }
  };

  // Background polling for an active bulk-send job
  const [activeJob, setActiveJob] = useState(null);
  const jobPollRef = React.useRef(null);

  useEffect(() => {
    // Clean up polling on unmount
    return () => { if (jobPollRef.current) clearInterval(jobPollRef.current); };
  }, []);

  const startJobPolling = (jobId) => {
    if (jobPollRef.current) clearInterval(jobPollRef.current);
    const poll = async () => {
      try {
        const { data } = await api.get(`/lists/${id}/email/send-bulk/status/${jobId}`);
        setActiveJob(data);
        if (["completed", "cancelled", "failed"].includes(data.status)) {
          clearInterval(jobPollRef.current); jobPollRef.current = null;
          await reload();
        }
      } catch (e) {
        // Transient network errors during polling are normal — ignore.
      }
    };
    poll();
    jobPollRef.current = setInterval(poll, 5000);
  };

  const cancelBulkJob = async () => {
    if (!activeJob?.id) return;
    if (!window.confirm("Annullare l'invio bulk in corso? Le email già inviate non verranno richiamate.")) return;
    try {
      await api.post(`/lists/${id}/email/send-bulk/cancel/${activeJob.id}`);
    } catch (e) {
      alert("Errore annullamento: " + (e.response?.data?.detail || e.message));
    }
  };

  const confirmBulkSend = async (opts = {}) => {
    const force = !!opts.force;
    if (!force) setSendModal(null);
    setBusy(true);
    try {
      const { data } = await api.post(
        `/lists/${id}/email/send-bulk`,
        { ...buildFilters(), force }
      );
      if (data.jobId) {
        // Background job mode: start polling
        setActiveJob({ id: data.jobId, status: "queued",
                        totals: { candidates: data.totalTargets },
                        progress: { sent_ok: 0, failed: 0, processed: 0 }});
        startJobPolling(data.jobId);
      } else {
        // No targets matched — show the result inline
        alert(data.message || "Nessun destinatario.");
        await reload();
      }
    } catch (e) {
      // Pre-flight blocked us: 409 with compromised analyses details
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 409 && det?.code === "COMPROMISED_ANALYSES") {
        const proceed = window.confirm(
          `${det.message}\n\n` +
          `OK  = Invia comunque a tutti ${det.totalTargets} destinatari\n` +
          `Annulla = Torna indietro e rianalizza prima`
        );
        if (proceed) {
          await confirmBulkSend({ force: true });
        } else {
          // Re-open the modal so user can see the warning + "Rianalizza" button
          await openSendModal();
        }
        return;
      }
      alert("Errore invio bulk: " + (det?.message || e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const requeueCompromisedFromModal = async () => {
    const ids = sendModal?.preview?.compromised?.listItemIds || [];
    if (ids.length === 0) return;
    if (!window.confirm(
      `Riaccodare ${ids.length} analisi? Ognuna costa qualche unit SEOZoom + 1 chiamata Lighthouse. ` +
      `Torna fra 10-15 minuti a inviare le email.`
    )) return;
    setBusy(true);
    try {
      await api.post(`/lists/${id}/items/analyze-bulk`, { itemIds: ids });
      setSendModal(null);
      alert(`${ids.length} analisi riaccodate in background. Torna fra qualche minuto per l'invio.`);
      await reload();
    } catch (e) {
      alert("Errore riaccodamento: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const downloadCsv = async (kind) => {
    // kind: "companies" | "engagement"
    try {
      const res = await api.get(`/export/lists/${id}/${kind}.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      a.download = match ? match[1] : `${kind}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Errore export: " + (e.response?.data?.detail || e.message));
    }
  };

  const bulkScrapeWebsites = async () => {
    const targets = selectedIds.length > 0 ? selectedIds : items.map(it => it.id);
    const companyIds = items.filter(it => targets.includes(it.id)).map(it => it.companyId);
    if (companyIds.length === 0) return;
    if (!window.confirm(`Trova sito web + scraping email per ${companyIds.length} aziende?\n\n• STEP 1: cerca il sito su Google Places + Brave Search + domain guess (gratis)\n• STEP 2: visita il sito e estrae le email (gratis)\n\nNessun credito OpenAPI consumato.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/companies/find-contacts", { companyIds, useOpenapiFallback: false });
      const bySrc = data.by_source || {};
      alert(
        `Operazione completata:\n\n` +
        `🔍 Discovery siti web:\n` +
        `• Trovati: ${data.web_search_hit || 0}\n` +
        `   - Google Places: ${bySrc.google_places || 0}\n` +
        `   - Brave Search: ${bySrc.brave_search || 0}\n` +
        `   - Domain guess: ${bySrc.domain_guess || 0}\n` +
        `   - Apify Maps: ${bySrc.apify_google_maps || 0}\n` +
        `• Non trovati: ${data.web_search_miss || 0}\n` +
        `• Già avevano sito: ${data.web_search_skip_have_site || 0}\n\n` +
        `📧 Scraping email:\n` +
        `• Siti con email trovate: ${data.scraped_ok || 0}\n` +
        `• Email promosse a primaria: ${data.emails_promoted || 0}\n` +
        `• Siti senza email: ${data.scraped_fail || 0}\n\n` +
        `📞 Telefoni aggiunti: ${data.phones_added || 0}`
      );
      await reload();
    } finally { setBusy(false); }
  };

  const resetEnrichment = async () => {
    if (!window.confirm(
      `♻️ Reset stato arricchimento della lista\n\n` +
      `Verranno svuotati TUTTI i campi di discovery/scraping (website, scrapedEmails, ` +
      `apifyData, ecc.) per le aziende di questa lista.\n\n` +
      `Manteniamo: nome, P.IVA, indirizzo, telefono, email primaria già impostata.\n\n` +
      `Utile per ripartire da zero dopo aver aggiornato il codice. Nessun costo.\n\n` +
      `Procedere?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/companies/reset-enrichment", { listId: list.id });
      alert(`✅ Reset completato. ${data.reset} aziende ripristinate (su ${data.scope}).`);
      await reload();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const bulkApifyEnrich = async () => {
    const targets = selectedIds.length > 0 ? selectedIds : items.map(it => it.id);
    const companyIds = items.filter(it => targets.includes(it.id)).map(it => it.companyId);
    if (companyIds.length === 0) return;
    const estCostUsd = (companyIds.length * 0.005).toFixed(2);
    const estMins = Math.ceil(companyIds.length * 30 / 60);
    const overwriteSite = window.confirm(
      `🌐 Approfondisci con Apify (Contact-Info-Scraper)\n\n` +
      `${companyIds.length} aziende verranno processate:\n` +
      `• Aziende con sito → SCRAPE del sito con browser headless (bypassa Cloudflare/anti-bot)\n` +
      `• Aziende senza sito → ricerca Google Maps come fallback\n\n` +
      `💰 Costo stimato: ~$${estCostUsd} USD\n` +
      `⏱ Durata: ~${estMins} min in background\n\n` +
      `🔄 Sovrascrivere siti esistenti (es. directory)?\n` +
      `OK = sì | Annulla = no\n\n` +
      `Conferma l'avvio?`
    );
    setBusy(true);
    try {
      const { data: enq } = await api.post("/crm/companies/apify-enrich", {
        companyIds, overwriteSite,
      });
      if (!enq.jobId) {
        alert("Errore avvio job: " + JSON.stringify(enq));
        return;
      }
      const jobId = enq.jobId;
      // Poll every 5s
      let lastStatus = "queued";
      const startTime = Date.now();
      while (true) {
        await new Promise(r => setTimeout(r, 5000));
        const { data: job } = await api.get(`/crm/companies/apify-enrich/job/${jobId}`);
        const cnt = job.counters || {};
        const elapsed = Math.round((Date.now() - startTime) / 1000);
        lastStatus = job.status;
        // Update UI status (simple console + busy state)
        console.log(`[Apify job ${jobId}] ${job.status} ${cnt.processed || 0}/${job.total} (${elapsed}s)`);
        if (job.status === "completed") {
            const errSamples = (job.errors_detail || []).slice(0, 5);
            alert(
              `✅ Apify completato!\n\n` +
              `Processate: ${cnt.processed || 0}/${job.total}\n` +
              `🎯 Successi: ${cnt.hit || 0} (scraper: ${cnt.via_contact_scraper||0}, maps: ${cnt.via_google_maps||0})\n` +
              `❌ Nessun risultato: ${cnt.no_results || 0}\n` +
              `⚠️ Errori: ${cnt.errors || 0}\n\n` +
              `🌐 Siti aggiunti: ${cnt.websites_added || 0}\n` +
              `🔄 Siti sovrascritti: ${cnt.websites_replaced || 0}\n` +
              `📧 Email aggiunte: ${cnt.emails_added || 0}\n` +
              `📞 Telefoni aggiunti: ${cnt.phones_added || 0}\n\n` +
              `💰 Costo: ~$${job.estimated_cost_usd}\n` +
              `⏱ Durata: ${elapsed}s` +
              (errSamples.length ? `\n\n⚠️ Primi errori:\n` + errSamples.map(e => `  • ${e.name}: ${e.error}`).join("\n") : "")
            );
            await reload();
            break;
          }
        if (job.status === "error") {
          alert("Errore Apify: " + (job.error || "unknown"));
          break;
        }
      }
    } catch (e) {
      alert("Errore Apify: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const cleanDirectoryWebsites = async () => {
    if (!window.confirm(
      `🧹 Pulisci siti directory dalla lista\n\n` +
      `Verranno svuotati i campi "sito web" di tutte le aziende il cui sito è un aggregatore/directory (paginegialle, cylex, icribis, misterimprese, informazione-aziende, facebook, google.com, ecc.).\n\n` +
      `Nessun costo. Dopo la pulizia rilancia "Trova siti + email GRATIS" o "Approfondisci con Apify" per ridiscovery.\n\n` +
      `Procedere?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/companies/clean-directory-websites", { listId: list.id });
      alert(
        `Pulizia completata:\n\n` +
        `• Aziende analizzate: ${data.scanned}\n` +
        `• Siti directory rimossi: ${data.cleaned}\n` +
        (data.samples?.length ? `\nEsempi rimossi:\n` + data.samples.map(s => `  - ${s.removed_website}`).join("\n") : "")
      );
      await reload();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const cleanUnrelatedWebsites = async () => {
    if (!window.confirm(
      `🧹🎯 Pulisci siti NON CORRELATI al nome\n\n` +
      `Verranno svuotati i siti che NON contengono alcuna parola significativa del nome azienda (es. "humanitas.it" salvato per "Centro Odontoiatrico").\n\n` +
      `⚠️ Può essere AGGRESSIVO: rimuove anche siti corretti dove brand ≠ nome legale (es. "studiorossi.it" per "STUDIO DENTISTICO DOTT. ROSSI SRL").\n\n` +
      `Usalo solo se sospetti molti siti sbagliati. Nessun costo.\n\n` +
      `Procedere?`
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/crm/companies/clean-unrelated-websites", { listId: list.id });
      alert(
        `Pulizia completata:\n\n` +
        `• Aziende analizzate: ${data.scanned}\n` +
        `• Siti non correlati rimossi: ${data.cleaned}\n` +
        (data.samples?.length ? `\nEsempi rimossi:\n` + data.samples.map(s => `  - ${s.name}: ${s.removed_website}`).join("\n") : "")
      );
      await reload();
    } catch (e) {
      alert("Errore: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (!list) return <div data-testid="list-loading"><span className="spinner"/></div>;

  return (
    <div data-testid="list-detail-page">
      <Link to="/liste" style={{display:"inline-flex",alignItems:"center",gap:6,color:"var(--mv-text-muted)",marginBottom:12}}><ChevronLeft size={14}/> Tutte le liste</Link>
      <div className="flex justify-between items-start mb-6 gap-4" style={{flexWrap:"wrap"}}>
        <div>
          <div className="h-display">{list.name}</div>
          {list.description && <div className="text-muted mt-2">{list.description}</div>}
          <div className="flex gap-2 mt-4">
            <span className="badge badge-gray">{items.length} aziende</span>
            <span className="badge badge-orange">{items.filter(i=>i.status==="screened").length} pre-screen</span>
            <span className="badge badge-green">{items.filter(i=>i.status==="analyzed").length} analizzate</span>
            <span className="badge badge-brand">{items.filter(i=>i.status==="email_sent").length} email</span>
          </div>
        </div>
        <div className="flex gap-2" style={{flexWrap:"wrap"}}>
          <Link to={`/liste/${id}/email-template`} className="btn btn-secondary" data-testid="edit-template-btn">
            <Mail size={16}/> Modifica template email
          </Link>
          <button onClick={()=>downloadCsv("companies")} className="btn btn-ghost" data-testid="export-companies-csv-btn"
            title="Scarica un CSV con tutte le aziende della lista, dati di arricchimento, score analisi, e status email">
            <Download size={16}/> Export aziende CSV
          </button>
          <button onClick={()=>downloadCsv("engagement")} className="btn btn-ghost" data-testid="export-engagement-csv-btn"
            title="Scarica un CSV con tutti gli eventi email della lista (open/click/bounce per destinatario)">
            <Download size={16}/> Export engagement CSV
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4" style={{flexWrap:"wrap"}}>
        <button onClick={bulkScrapeWebsites} disabled={busy || items.length===0} className="btn btn-ghost btn-sm" data-testid="bulk-scrape-list-btn"
          title="STEP 1: trova il sito web (Google Places + Brave + domain guess). STEP 2: scraping email dal sito. Tutto GRATIS, nessun credito OpenAPI.">
          <Sparkles size={14}/> Trova siti + email GRATIS ({selectedIds.length || items.length})
        </button>
        <button onClick={bulkApifyEnrich} disabled={busy || items.length===0} className="btn btn-ghost btn-sm" data-testid="bulk-apify-btn"
          title="A PAGAMENTO (~$0.005/azienda): visita il SITO dell'azienda con browser headless via Apify Contact-Info-Scraper. Bypassa Cloudflare/anti-bot, estrae email/phone/social. Per aziende senza sito usa Google Maps come fallback."
          style={{borderColor:"#0ea5e9",color:"#0ea5e9"}}>
          <MapPin size={14}/> Approfondisci con Apify (~${((selectedIds.length || items.length) * 0.005).toFixed(2)})
        </button>
        <button onClick={resetEnrichment} disabled={busy || items.length===0} className="btn btn-ghost btn-sm" data-testid="reset-enrichment-btn"
          title="Resetta tutti i campi di arricchimento (website, scrapedEmails, apifyData) della lista. Mantiene nome/PIVA/indirizzo. Utile per ripartire da zero dopo update codice.">
          <RefreshCw size={14}/> Reset stato arricchimento
        </button>
        <button onClick={cleanDirectoryWebsites} disabled={busy || items.length===0} className="btn btn-ghost btn-sm" data-testid="clean-directories-btn"
          title="Svuota i campi 'sito' di tutte le aziende il cui sito è una directory/aggregatore (cylex, icribis, ecc.). Nessun costo. Poi rilancia 'Trova siti + email GRATIS' per re-discovery con il blocklist aggiornato.">
          <Trash2 size={14}/> Pulisci siti directory
        </button>
        <button onClick={cleanUnrelatedWebsites} disabled={busy || items.length===0} className="btn btn-ghost btn-sm" data-testid="clean-unrelated-btn"
          title="Svuota i campi 'sito' che NON contengono alcuna parola del nome azienda (es. humanitas.it per 'Centro Odontoiatrico'). AGGRESSIVO: può rimuovere anche siti corretti se brand≠nome legale. Nessun costo."
          style={{color:"#dc2626"}}>
          <Trash2 size={14}/> Pulisci siti non correlati
        </button>
        <button onClick={verifyAllEmails} disabled={busy || items.length===0}
          className="btn btn-ghost btn-sm" data-testid="verify-emails-btn"
          title="Verifica tutte le email della lista tramite ZeroBounce (deliverability). Costo ~€0,007 per email. Avviato in background.">
          <Mail size={14}/> Verifica email ZeroBounce
        </button>
        <button onClick={prescreen} disabled={selectedIds.length===0||busy} className="btn btn-secondary btn-sm" data-testid="prescreen-btn">
          <RefreshCw size={14}/> Pre-screen SEO ({selectedIds.length})
        </button>
        <button onClick={analyze} disabled={selectedIds.length===0||busy} className="btn btn-primary btn-sm" data-testid="analyze-btn">
          <Play size={14}/> Avvia analisi profonda ({selectedIds.length})
        </button>
        <button onClick={openSendModal} disabled={busy || items.length===0} className="btn btn-primary btn-sm" data-testid="bulk-send-btn"
          title="Configura filtri (solo target verdi, solo con titolare, solo non contattate negli ultimi N giorni) e invia in massa.">
          <Send size={14}/> Invia email mirata{selectedIds.length > 0 ? ` (${selectedIds.length} selez.)` : ""}
        </button>
        {selectedIds.length > 0 && (
          <button onClick={removeManyFromList} disabled={busy} className="btn btn-ghost btn-sm"
            data-testid="bulk-remove-btn"
            title="Rimuove le aziende selezionate da QUESTA lista. Restano nel CRM."
            style={{color:"var(--mv-danger)",borderColor:"var(--mv-danger)"}}>
            <Trash2 size={14}/> Rimuovi {selectedIds.length} dalla lista
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="card text-center" style={{padding:40}}>
          <div className="text-muted">Nessuna azienda in questa lista. Aggiungile da <Link to="/ricerca">Ricerca</Link>.</div>
        </div>
      ) : (
        <>
          {/* ---- VERDICT TOOLBAR (filter + bulk actions per outcome) ---- */}
          <div className="card mb-3" data-testid="verdict-toolbar" style={{padding:"10px 14px"}}>
            <div className="flex items-center gap-3" style={{flexWrap:"wrap"}}>
              <div className="text-small" style={{fontWeight:700, color:"var(--mv-text)"}}>
                Filtra per verdetto:
              </div>
              {[
                {key:"all",       label:"Tutti",            cnt: items.length,                color:"#64748b"},
                {key:"target",    label:"🎯 Target ideale",  cnt: verdictCounts.target,        color:"#15803d"},
                {key:"review",    label:"⚠️ Da valutare",    cnt: verdictCounts.review,        color:"#b45309"},
                {key:"skip",      label:"⛔ Skip",           cnt: verdictCounts.skip,          color:"#b91c1c"},
                {key:"no_website",label:"🚫 No sito",        cnt: verdictCounts.no_website,    color:"#475569"},
                {key:"none",      label:"⏳ Non screenato",   cnt: verdictCounts.none,          color:"#94a3b8"},
              ].filter(p => p.key === "all" || p.cnt > 0).map(p => {
                const active = verdictFilter === p.key;
                return (
                  <button key={p.key}
                    onClick={()=>setVerdictFilter(p.key)}
                    data-testid={`verdict-filter-${p.key}`}
                    style={{
                      display:"inline-flex", alignItems:"center", gap:6,
                      padding:"5px 11px", borderRadius:999,
                      border: `1.5px solid ${active ? p.color : "var(--mv-border)"}`,
                      background: active ? `${p.color}15` : "#fff",
                      color: active ? p.color : "var(--mv-text)",
                      fontSize:12, fontWeight: active ? 700 : 600,
                      cursor: "pointer",
                    }}>
                    {p.label}
                    <span style={{
                      background: active ? p.color : "var(--mv-bg-soft)",
                      color: active ? "#fff" : "var(--mv-text-muted)",
                      padding:"1px 7px", borderRadius:999,
                      fontSize:11, fontWeight:700, minWidth:18, textAlign:"center",
                    }}>{p.cnt}</span>
                  </button>
                );
              })}

              <div style={{flex:1}}/>

              <button onClick={()=>selectByVerdict(["skip"])} disabled={busy || verdictCounts.skip === 0}
                className="btn btn-ghost btn-sm" data-testid="select-skip-btn"
                title="Aggiunge ai selezionati tutti i contatti con verdetto SKIP">
                ✓ Seleziona Skip ({verdictCounts.skip})
              </button>
              <button onClick={()=>selectByVerdict(["review"])} disabled={busy || verdictCounts.review === 0}
                className="btn btn-ghost btn-sm" data-testid="select-review-btn"
                title="Aggiunge ai selezionati tutti i contatti con verdetto DA VALUTARE">
                ✓ Seleziona Da valutare ({verdictCounts.review})
              </button>
              <button onClick={()=>selectByVerdict(["skip", "review"])} disabled={busy || (verdictCounts.skip + verdictCounts.review) === 0}
                className="btn btn-ghost btn-sm" data-testid="select-skip-review-btn"
                title="Aggiunge ai selezionati Skip + Da valutare">
                ✓ Skip + Da valutare ({verdictCounts.skip + verdictCounts.review})
              </button>
              <button
                onClick={()=>removeByVerdict(["skip", "review"], "SKIP + DA VALUTARE")}
                disabled={busy || (verdictCounts.skip + verdictCounts.review) === 0}
                className="btn btn-sm" data-testid="purge-non-target-btn"
                title="Rimuove in un colpo solo tutti i contatti Skip + Da valutare. Resta solo chi è in target."
                style={{
                  background:"#fef2f2", color:"#b91c1c",
                  borderColor:"#fecaca", fontWeight:700,
                }}>
                <Trash2 size={14}/> Pulisci non-target ({verdictCounts.skip + verdictCounts.review})
              </button>
            </div>
          </div>

          {/* ---- BULK SEND JOB PROGRESS BANNER ---- */}
          {activeJob && ["queued","running","completed","cancelled"].includes(activeJob.status) && (
            <div className="card mb-3" data-testid="bulk-job-banner" style={{
              padding:"12px 16px",
              background: activeJob.status === "completed" ? "#dcfce7" :
                            activeJob.status === "cancelled" ? "#fef3c7" : "#dbeafe",
              borderLeft: `4px solid ${
                activeJob.status === "completed" ? "#15803d" :
                activeJob.status === "cancelled" ? "#b45309" : "#0369a1"}`,
            }}>
              <div className="flex items-center gap-3" style={{flexWrap:"wrap"}}>
                <div style={{flex:"1 1 240px"}}>
                  <div className="text-small" style={{fontWeight:700, color:
                    activeJob.status === "completed" ? "#166534" :
                    activeJob.status === "cancelled" ? "#92400e" : "#0c4a6e"
                  }}>
                    {activeJob.status === "queued" && "📨 Invio in coda…"}
                    {activeJob.status === "running" && "📨 Invio in corso — puoi chiudere la pagina, continuerà in background"}
                    {activeJob.status === "completed" && "✅ Invio bulk completato"}
                    {activeJob.status === "cancelled" && "⛔ Invio annullato dall'utente"}
                  </div>
                  <div className="text-small text-muted" style={{marginTop:4}}>
                    Inviate <strong>{activeJob.progress?.sent_ok || 0}</strong> · Fallite <strong>{activeJob.progress?.failed || 0}</strong>
                    {" · "}Processate <strong>{activeJob.progress?.processed || 0}</strong> / {activeJob.totals?.candidates || 0}
                  </div>
                  {(activeJob.status === "running" || activeJob.status === "queued") && (
                    <div style={{marginTop:6, height:6, background:"rgba(0,0,0,0.08)", borderRadius:3, overflow:"hidden"}}>
                      <div style={{
                        width: `${Math.round(((activeJob.progress?.processed || 0) / Math.max(1, activeJob.totals?.candidates || 1)) * 100)}%`,
                        height:"100%", background:"#0369a1", transition:"width 0.4s",
                      }}/>
                    </div>
                  )}
                </div>
                {(activeJob.status === "running" || activeJob.status === "queued") && (
                  <button className="btn btn-sm" onClick={cancelBulkJob}
                    data-testid="cancel-bulk-job-btn"
                    style={{background:"#fef2f2", color:"#b91c1c", borderColor:"#fecaca"}}>
                    Annulla invio
                  </button>
                )}
                {["completed","cancelled"].includes(activeJob.status) && (
                  <button className="btn-icon" title="Chiudi"
                    onClick={()=>setActiveJob(null)} data-testid="dismiss-bulk-job">×</button>
                )}
              </div>
              {activeJob.errors && activeJob.errors.length > 0 && activeJob.status !== "queued" && (
                <details style={{marginTop:8}}>
                  <summary className="text-small text-muted" style={{cursor:"pointer"}}>
                    Ultimi errori ({activeJob.errors.length})
                  </summary>
                  <ul className="text-small text-muted" style={{margin:"6px 0 0", paddingLeft:20, maxHeight:120, overflowY:"auto"}}>
                    {activeJob.errors.slice(-10).map((err, idx) => <li key={idx}>{err}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 mb-3" style={{flexWrap:"wrap"}}>
            <div style={{position:"relative", flex:"1 1 320px", maxWidth:480}}>
              <Search size={14} style={{position:"absolute", left:10, top:"50%",
                transform:"translateY(-50%)", color:"var(--mv-text-muted)"}}/>
              <input
                type="search"
                value={searchQuery}
                onChange={e=>setSearchQuery(e.target.value)}
                placeholder="Cerca per nome, sito, email, città, P.IVA, telefono…"
                data-testid="list-search-input"
                style={{
                  width:"100%", padding:"8px 12px 8px 32px",
                  border:"1px solid var(--mv-border)", borderRadius:6, fontSize:13,
                }}/>
              {searchQuery && (
                <button onClick={()=>setSearchQuery("")} className="btn-icon"
                  style={{position:"absolute", right:4, top:"50%", transform:"translateY(-50%)"}}
                  data-testid="list-search-clear">
                  <X size={13}/>
                </button>
              )}
            </div>
            <div className="text-small text-muted">
              {searchQuery || verdictFilter !== "all" ? (
                <>{filteredItems.length} su {items.length} aziende</>
              ) : (
                <>{items.length} aziende</>
              )}
              {totalPages > 1 && <> · Pagina {safePage} di {totalPages}</>}
            </div>
            {totalPages > 1 && (
              <div className="flex gap-1 ml-auto" data-testid="list-pagination">
                <button onClick={()=>setCurrentPage(1)} disabled={safePage<=1}
                  className="btn btn-ghost btn-sm" title="Prima pagina">«</button>
                <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={safePage<=1}
                  className="btn btn-ghost btn-sm" data-testid="page-prev">‹</button>
                <span style={{
                  padding:"4px 10px", fontSize:13, fontWeight:600,
                  background:"var(--mv-bg-soft)", borderRadius:4,
                  minWidth:60, textAlign:"center",
                }}>{safePage} / {totalPages}</span>
                <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={safePage>=totalPages}
                  className="btn btn-ghost btn-sm" data-testid="page-next">›</button>
                <button onClick={()=>setCurrentPage(totalPages)} disabled={safePage>=totalPages}
                  className="btn btn-ghost btn-sm" title="Ultima pagina">»</button>
              </div>
            )}
          </div>

        <div className="card" style={{padding:0,overflow:"auto"}}>
          <table className="tbl" data-testid="list-items-table">
            <thead>
              <tr>
                <th style={{width:30}}><input type="checkbox" checked={allSel} onChange={toggleAll} data-testid="select-all-items"/></th>
                <th>Azienda</th>
                <th>Sito</th>
                <th>Verdetto</th>
                <th>Analisi</th>
                <th>Score</th>
                <th>Traffico / ZA</th>
                <th>Email</th>
                <th>Conv.</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.length === 0 && (searchQuery || verdictFilter !== "all") && (
                <tr><td colSpan={10} style={{padding:30, textAlign:"center"}}
                  className="text-muted">
                  {searchQuery
                    ? <>Nessuna azienda corrisponde a "{searchQuery}"</>
                    : <>Nessuna azienda con questo verdetto.</>}
                </td></tr>
              )}
              {pagedItems.map(it => {
                const c = it.company || {};
                const a = it.analysis;
                const e = it.email;
                const sum = a?.summary;
                const owner = c.owner;
                const domain = c.website ? c.website.replace(/^https?:\/\//, "").replace(/\/$/, "") : null;
                return (
                  <tr key={it.id} data-testid={`item-row-${it.id}`}>
                    <td><input type="checkbox" checked={!!selected[it.id]} onChange={()=>toggle(it.id)} /></td>
                    <td>
                      <div style={{fontWeight:600}}>{c.companyName}</div>
                      <div className="text-small text-muted">
                        {c.city} ({c.province}) · P.IVA {c.vatCode}
                      </div>
                      {owner?.fullName && (
                        <div className="text-small" style={{color:"var(--mv-primary)",marginTop:2}}>
                          <UserCheck size={11} style={{display:"inline",verticalAlign:"middle"}}/> {owner.fullName}
                          {owner.role && <span className="text-muted"> · {owner.role.split(',')[0]}</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      {domain ? <a href={`https://${domain}`} target="_blank" rel="noreferrer" className="text-small">{domain}</a> : <span className="text-muted text-small">—</span>}
                      <div className="text-small text-muted" style={{marginTop:2}}>
                        {c.email ? <span style={{color:"var(--mv-success)"}}>{c.email}</span> : c.pec ? <span title={c.pec}>PEC: {c.pec.split('@')[0]}…</span> : <span>—</span>}
                      </div>
                    </td>
                    <td><TrafficLight v={it.trafficLight} /></td>
                    <td style={{minWidth:140}}>
                      {!a ? <span className="text-muted">—</span> :
                       a.status === "completed" ? (
                         <div>
                           <span className="badge badge-green">completata</span>
                           {it.quality?.suspicious && (
                             <div className="text-small" data-testid={`quality-badge-${it.id}`}
                               title={`Dati sospetti:\n· ${it.quality.reasons.join("\n· ")}`}
                               style={{color:"#b45309",fontWeight:600,marginTop:2,cursor:"help"}}>
                               ⚠ Dati sospetti ({it.quality.reasons.length})
                             </div>
                           )}
                           {a.analyzedDomain && <div className="text-small text-muted" style={{marginTop:2}}>{a.analyzedDomain}</div>}
                         </div>
                       ) : a.status === "running" ? <span className="badge badge-orange"><span className="spinner" style={{width:10,height:10}}/> in corso</span>
                       : a.status === "queued" ? <span className="badge badge-gray">in coda</span>
                       : <span className="badge badge-red" title={a.errorMessage}>fallita</span>}
                    </td>
                    <td>
                      {sum?.score_globale != null ? (
                        <div style={{textAlign:"center"}}>
                          <div style={{fontWeight:800,fontSize:18,color: sum.score_globale >= 75 ? "var(--mv-success)" : sum.score_globale >= 50 ? "var(--mv-warning)" : "var(--mv-danger)"}}>
                            {sum.score_globale}
                          </div>
                          <div className="text-small text-muted">/100</div>
                        </div>
                      ) : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-small">
                      {(() => {
                        const m = sum?.metrics || {};
                        const traf = m.traffic_monthly ?? it.estTraffic;
                        const za = m.z_authority ?? it.zAuthority;
                        if (traf == null && za == null) return <span className="text-muted">—</span>;
                        const zaStr = (za != null && za.toFixed) ? za.toFixed(1) : (za ?? "—");
                        return <>{fmt(traf ?? 0)} · ZA {zaStr}</>;
                      })()}
                    </td>
                    <td className="text-small">
                      {e ? (e.status === "sent" ? <span className="badge badge-brand">inviata</span> : <span className="badge badge-red">{e.status}</span>) : <span className="text-muted">—</span>}
                    </td>
                    <td className="text-small">{a?.calendarClicks || 0}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={()=>openEdit(it)} className="btn btn-ghost btn-sm"
                          title="Modifica dati azienda (sito, email, telefono, PEC)"
                          data-testid={`edit-${it.id}`}>
                          <Pencil size={14}/>
                        </button>
                        {a?.status === "completed" && (
                          <a href={`/analisi/${a.publicToken}`} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="Vedi analisi" data-testid={`view-analysis-${it.id}`}><Eye size={14}/></a>
                        )}
                        {a?.status === "completed" && (c.email || c.pec) && (
                          <>
                          <button onClick={()=>openPreview(it)} className="btn btn-ghost btn-sm" title="Anteprima email template (con PDF allegato)" data-testid={`preview-email-${it.id}`}><Mail size={14}/></button>
                          <button onClick={()=>sendEmailDirect(it)} className="btn btn-ghost btn-sm"
                            title="Invia email subito (senza anteprima)"
                            data-testid={`send-direct-${it.id}`}
                            style={{color:"#0ea5e9"}}>
                            <Send size={14}/>
                          </button>
                          </>
                        )}
                        {a?.status === "failed" && (
                          <button onClick={()=>retryOne(it)} className="btn btn-ghost btn-sm" title="Riprova"><RefreshCw size={14}/></button>
                        )}
                        <button onClick={()=>removeFromList(it)} className="btn btn-ghost btn-sm"
                          title="Rimuovi dalla lista (resta nel CRM)"
                          data-testid={`remove-${it.id}`}
                          style={{color:"var(--mv-danger)"}}>
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-3" style={{padding:"8px 0"}}>
            <button onClick={()=>setCurrentPage(1)} disabled={safePage<=1}
              className="btn btn-ghost btn-sm">«</button>
            <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={safePage<=1}
              className="btn btn-ghost btn-sm">‹ Precedente</button>
            <span className="text-small text-muted" style={{padding:"0 12px"}}>
              {safePage} / {totalPages}
            </span>
            <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={safePage>=totalPages}
              className="btn btn-ghost btn-sm">Successiva ›</button>
            <button onClick={()=>setCurrentPage(totalPages)} disabled={safePage>=totalPages}
              className="btn btn-ghost btn-sm">»</button>
          </div>
        )}
        </>
      )}

      {previewModal && (
        <div className="modal-backdrop" onClick={()=>setPreviewModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="h-section">Anteprima email completa</div>
                <div className="text-small text-muted">L'invio reale userà esattamente questa email e questo allegato.</div>
              </div>
              <button onClick={()=>setPreviewModal(null)} className="btn btn-ghost btn-sm">Chiudi</button>
            </div>

            <div className="card" style={{padding:14, marginBottom:14, background:"var(--mv-bg-soft)"}}>
              <div style={{display:"grid", gridTemplateColumns:"auto 1fr", gap:"4px 12px", fontSize:13}}>
                <div className="text-muted">Da:</div>
                <div><strong>{previewModal.fromName}</strong>{previewModal.replyTo ? <span className="text-muted"> · reply-to: {previewModal.replyTo}</span> : null}</div>
                <div className="text-muted">A:</div>
                <div style={{fontWeight:600}}>{previewModal.toEmail}</div>
                <div className="text-muted">Oggetto:</div>
                <div style={{fontWeight:600}}>{previewModal.subject}</div>
                <div className="text-muted">Allegato:</div>
                <div>
                  {previewModal.pdfAttachment ? (
                    <a href={previewModal.pdfAttachment.url}
                       target="_blank" rel="noreferrer"
                       data-testid="preview-pdf-link"
                       style={{display:"inline-flex", alignItems:"center", gap:6,
                         background:"#fff", padding:"4px 10px", border:"1px solid var(--mv-border)",
                         borderRadius:6, color:"var(--mv-primary)", textDecoration:"none", fontWeight:600}}>
                      <Download size={13}/> {previewModal.pdfAttachment.filename}
                      <span className="text-small text-muted" style={{fontWeight:400}}> · clicca per aprire</span>
                    </a>
                  ) : (
                    <span className="badge badge-orange">Nessun PDF — analisi non completata</span>
                  )}
                </div>
              </div>
            </div>

            <div className="text-small text-muted" style={{fontWeight:600, textTransform:"uppercase", marginBottom:4}}>
              Corpo email
            </div>
            <iframe srcDoc={previewModal.html} title="preview" style={{width:"100%",height:380,border:"1px solid var(--mv-border)",borderRadius:8,background:"white"}} data-testid="preview-iframe"/>

            {previewModal.pdfAttachment && (
              <>
                <div className="text-small text-muted" style={{fontWeight:600, textTransform:"uppercase", marginTop:14, marginBottom:4}}>
                  Anteprima PDF allegato
                </div>
                <iframe src={previewModal.pdfAttachment.url + "#view=FitH"}
                  title="pdf preview"
                  style={{width:"100%", height:520, border:"1px solid var(--mv-border)",
                    borderRadius:8, background:"#525659"}}
                  data-testid="preview-pdf-iframe"/>
              </>
            )}

            <div className="flex justify-between mt-4">
              <Link to={`/liste/${id}/email-template`} className="btn btn-secondary btn-sm">Modifica template</Link>
              <button onClick={()=>sendEmail(previewModal.item)} disabled={busy} className="btn btn-primary" data-testid="send-email-confirm">
                {busy ? <span className="spinner"/> : <><Send size={14}/> Invia ora</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {sendModal && (
        <div className="modal-backdrop" onClick={()=>setSendModal(null)}>
          <div className="modal modal-lg" onClick={e=>e.stopPropagation()} data-testid="bulk-send-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="h-section">📨 Invio email mirata</div>
                <div className="text-small text-muted mt-1">
                  Seleziona i filtri, vedi quanti destinatari escono, conferma.
                </div>
              </div>
              <button onClick={()=>setSendModal(null)} className="btn btn-ghost btn-sm">✕</button>
            </div>

            <div className="grid gap-3" style={{gridTemplateColumns:"1fr 1fr"}}>
              <div className="card" style={{padding:14}}>
                <div className="stat-label mb-3">Filtri intelligenti</div>
                <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                  <input type="checkbox" data-testid="filter-only-green"
                    checked={sendFilters.onlyGreenTarget}
                    onChange={e=>setSendFilters({...sendFilters, onlyGreenTarget: e.target.checked})}/>
                  <span>🎯 Solo target verdi (pre-screen "target")</span>
                </label>
                <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                  <input type="checkbox" data-testid="filter-require-owner"
                    checked={sendFilters.requireOwner}
                    onChange={e=>setSendFilters({...sendFilters, requireOwner: e.target.checked})}/>
                  <span>👤 Solo con titolare identificato</span>
                </label>
                <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                  <input type="checkbox" data-testid="filter-not-contacted"
                    checked={sendFilters.notContactedEnabled}
                    onChange={e=>setSendFilters({...sendFilters, notContactedEnabled: e.target.checked})}/>
                  <span>⏱️ Solo non contattate negli ultimi</span>
                  <input type="number" min="1" max="365" data-testid="filter-not-contacted-days"
                    value={sendFilters.notContactedDays} disabled={!sendFilters.notContactedEnabled}
                    onChange={e=>setSendFilters({...sendFilters, notContactedDays: parseInt(e.target.value)||30})}
                    style={{width:60, padding:"2px 6px", border:"1px solid #ddd", borderRadius:4}}/>
                  <span>giorni</span>
                </label>
              </div>

              <div className="card" style={{padding:14}}>
                <div className="stat-label mb-3">Opzioni invio</div>
                <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                  <input type="checkbox" data-testid="filter-require-analysis"
                    checked={sendFilters.requireAnalysis}
                    onChange={e=>setSendFilters({...sendFilters, requireAnalysis: e.target.checked})}/>
                  <span>📊 Richiedi analisi completata (consigliato)</span>
                </label>
                <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                  <input type="checkbox" data-testid="filter-include-pec"
                    checked={sendFilters.includePec}
                    onChange={e=>setSendFilters({...sendFilters, includePec: e.target.checked})}/>
                  <span>📮 Fallback su PEC se manca email</span>
                </label>

                <label className="flex gap-2 items-start mb-2 mt-2" style={{cursor:"pointer", padding:8, background:"#faf5ff", borderRadius:6, border:"1px solid #ddd6fe"}}>
                  <input type="checkbox" data-testid="filter-use-ai"
                    checked={sendFilters.useAiEmails}
                    onChange={e=>setSendFilters({...sendFilters, useAiEmails: e.target.checked})}
                    style={{marginTop:3}}/>
                  <div>
                    <span style={{fontWeight:600,color:"#5b21b6"}}>✨ Usa email AI iper-personalizzate</span>
                    <div className="text-small text-muted" style={{lineHeight:1.5}}>
                      Per ogni lead userà la mail AI generata (se presente). Se non c'è, fallback automatico al template della lista. Genera prima con il pulsante "Genera email AI".
                    </div>
                  </div>
                </label>

                <div className="stat-label mt-4 mb-2" style={{display:"flex",alignItems:"center",gap:6}}>
                  ✉ Verifica email (ZeroBounce)
                </div>
                <label className="text-small mb-2" style={{display:"block"}}>
                  Filtro per status:
                  <select className="input" style={{marginTop:4,fontSize:13}}
                    data-testid="filter-verify-mode"
                    value={sendFilters.verifyFilter}
                    onChange={e=>setSendFilters({...sendFilters, verifyFilter: e.target.value})}>
                    <option value="off">Nessun filtro (invia a tutte)</option>
                    <option value="exclude_bad">Escludi solo invalid/spamtrap/abuse/do_not_mail (consigliato)</option>
                    <option value="only_valid">Solo email con status="valid" (massima sicurezza)</option>
                  </select>
                </label>
                {sendFilters.verifyFilter !== "off" && (
                  <>
                    <label className="text-small mb-2" style={{display:"block"}}>
                      Score minimo ZeroBounce: <strong>{sendFilters.verifyMinScore}</strong>/100
                      <input type="range" min="0" max="100" step="5"
                        data-testid="filter-verify-score"
                        value={sendFilters.verifyMinScore}
                        onChange={e=>setSendFilters({...sendFilters, verifyMinScore: parseInt(e.target.value)})}
                        style={{width:"100%",marginTop:4}}/>
                      <div className="text-small text-muted" style={{display:"flex",justifyContent:"space-between",fontSize:10}}>
                        <span>0 (tutti)</span><span>80 (consigliato)</span><span>95 (top)</span>
                      </div>
                    </label>
                    <label className="flex gap-2 items-center mb-2" style={{cursor:"pointer"}}>
                      <input type="checkbox" data-testid="filter-require-verified"
                        checked={sendFilters.requireVerified}
                        onChange={e=>setSendFilters({...sendFilters, requireVerified: e.target.checked})}/>
                      <span className="text-small">Escludi anche le email <strong>mai verificate</strong></span>
                    </label>
                  </>
                )}
                <div className="text-small text-muted mt-2">
                  Invio throttling: max 10 email/minuto (limite Resend).
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <button onClick={refreshPreview} disabled={busy} className="btn btn-secondary btn-sm" data-testid="refresh-preview-btn">
                <RefreshCw size={14}/> Ricalcola preview
              </button>
            </div>

            <div className="card mt-4" style={{padding:18,background:"var(--mv-primary-bg, #fff5ed)",borderLeft:"4px solid var(--mv-primary)"}} data-testid="bulk-send-preview">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="text-small text-muted">Destinatari finali</div>
                  <div style={{fontSize:36,fontWeight:700,color:"var(--mv-primary)"}} data-testid="will-send-count">
                    {sendModal.preview.will_send}
                  </div>
                  <div className="text-small text-muted">su {sendModal.preview.candidates} candidati</div>
                </div>
                <div className="text-small" style={{textAlign:"right",lineHeight:1.7}}>
                  <span className="text-muted">○ Senza email: {sendModal.preview.skip_no_email}</span><br/>
                  <span className="text-muted">○ Senza analisi: {sendModal.preview.skip_no_analysis}</span><br/>
                  <span className="text-muted">○ Senza titolare: {sendModal.preview.skip_no_owner}</span><br/>
                  <span className="text-muted">○ Recente: {sendModal.preview.skip_recently_contacted}</span>
                  {(sendModal.preview.skip_verify_bad_status || 0) > 0 && (
                    <><br/><span style={{color:"var(--mv-danger)"}}>✗ Email non valida: {sendModal.preview.skip_verify_bad_status}</span></>
                  )}
                  {(sendModal.preview.skip_verify_low_score || 0) > 0 && (
                    <><br/><span style={{color:"#92400e"}}>⚠ Score basso: {sendModal.preview.skip_verify_low_score}</span></>
                  )}
                  {(sendModal.preview.skip_verify_unverified || 0) > 0 && (
                    <><br/><span className="text-muted">? Mai verificate: {sendModal.preview.skip_verify_unverified}</span></>
                  )}
                </div>
              </div>
              {sendModal.preview.sample?.length > 0 && (
                <div className="mt-3">
                  <div className="text-small text-muted mb-1">Anteprima (prime 5):</div>
                  <ul className="text-small" style={{marginLeft:18,lineHeight:1.7}}>
                    {sendModal.preview.sample.map((s,i)=>(
                      <li key={i}><strong>{s.companyName}</strong> → {s.toEmail}
                        {s.owner && <span className="text-muted"> · {s.owner}</span>}
                        {s.trafficLight === "target" && <span className="badge badge-green" style={{marginLeft:6}}>target</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(sendModal.preview.compromised?.count || 0) > 0 && (
                <div className="mt-3" style={{
                  padding: 14, background: "#FEF3F2",
                  border: "1px solid #FECDCA", borderRadius: 8,
                }} data-testid="bulk-send-compromised-warning">
                  <div className="flex items-start gap-2 mb-2">
                    <span style={{fontSize:20}}>⚠️</span>
                    <div>
                      <strong style={{color:"#B42318"}}>
                        {sendModal.preview.compromised.count} destinatari con dati sospetti
                      </strong>
                      <div className="text-small" style={{color:"#7A271A",marginTop:4}}>
                        Le loro analisi contengono valori mock (Lighthouse) o
                        SEOZoom variante errata. Se invii ora, il cliente riceve
                        numeri sbagliati.
                      </div>
                    </div>
                  </div>
                  <ul className="text-small" style={{marginLeft:24,lineHeight:1.6,color:"#7A271A"}}>
                    {sendModal.preview.compromised.samples.slice(0,5).map((s,i)=>(
                      <li key={i}>
                        <strong>{s.companyName || s.website}</strong>
                        <span className="text-muted"> · {s.reasons.join(", ")}</span>
                      </li>
                    ))}
                    {sendModal.preview.compromised.count > 5 && (
                      <li className="text-muted">…e altri {sendModal.preview.compromised.count - 5}</li>
                    )}
                  </ul>
                  <button
                    onClick={requeueCompromisedFromModal}
                    className="btn btn-primary mt-2"
                    disabled={busy}
                    data-testid="bulk-send-requeue-compromised-btn"
                  >
                    Rianalizza {sendModal.preview.compromised.count} e torna
                  </button>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-6">
              <button onClick={()=>setSendModal(null)} className="btn btn-secondary" style={{flex:1}} data-testid="bulk-send-cancel">Annulla</button>
              <button onClick={()=>confirmBulkSend()} disabled={sendModal.preview.will_send === 0 || busy}
                className="btn btn-primary" style={{flex:2}} data-testid="bulk-send-confirm">
                {sendModal.preview.will_send === 0
                  ? "Nessun destinatario valido"
                  : `🚀 Invia ora a ${sendModal.preview.will_send} aziende`}
              </button>
            </div>
          </div>
        </div>
      )}

      {zbProgress && (
        <div className="card mb-3" style={{padding:12,
          background: zbProgress.status === "completed" ? "#f0fdf4" :
                       zbProgress.status === "failed" ? "#fef2f2" : "#eff6ff",
          borderLeft: `4px solid ${zbProgress.status === "completed" ? "#15803d" :
                                     zbProgress.status === "failed" ? "#b91c1c" : "#3b82f6"}`}}
          data-testid="zb-progress-banner">
          <div className="flex justify-between items-center" style={{gap:12}}>
            <div className="flex items-center gap-2">
              {zbProgress.status === "running" && <span className="spinner" style={{width:14,height:14}}/>}
              <div>
                <strong>Verifica ZeroBounce: {zbProgress.done}/{zbProgress.total}</strong>
                {zbProgress.status === "completed" && (
                  <span style={{marginLeft:8, color:"#15803d", fontWeight:600}}>✓ Completata</span>
                )}
                <div className="text-small text-muted" style={{marginTop:2}}>
                  Verificate: {zbProgress.verified || 0} · Da cache: {zbProgress.cached || 0}
                  {(zbProgress.by_status?.valid ?? 0) > 0 && <> · ✓ valide: {zbProgress.by_status.valid}</>}
                  {(zbProgress.by_status?.invalid ?? 0) > 0 && <> · ✗ invalide: {zbProgress.by_status.invalid}</>}
                  {(zbProgress.by_status?.["catch-all"] ?? 0) > 0 && <> · catch-all: {zbProgress.by_status["catch-all"]}</>}
                  {(zbProgress.no_email || 0) > 0 && <> · senza email: {zbProgress.no_email}</>}
                  {(zbProgress.errors || 0) > 0 && <span style={{color:"#b91c1c"}}> · errori: {zbProgress.errors}</span>}
                </div>
              </div>
            </div>
            <button onClick={()=>setZbProgress(null)} className="btn btn-ghost btn-sm">
              <X size={14}/>
            </button>
          </div>
          {zbProgress.status === "running" && zbProgress.total > 0 && (
            <div style={{marginTop:8, background:"#e5e7eb", height:6, borderRadius:3, overflow:"hidden"}}>
              <div style={{
                width: `${Math.round((zbProgress.done / zbProgress.total) * 100)}%`,
                height:"100%", background:"#3b82f6", transition:"width 0.5s",
              }}/>
            </div>
          )}
        </div>
      )}

      {editModal && (
        <div className="modal-backdrop" onClick={()=>setEditModal(null)} data-testid="edit-modal">
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth: 540}}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="h-section">Modifica dati azienda</div>
                <div className="text-small text-muted">{editModal.item.company?.companyName}</div>
              </div>
              <button onClick={()=>setEditModal(null)} className="btn btn-ghost btn-sm" data-testid="edit-modal-close"><X size={16}/></button>
            </div>

            <div style={{display:"grid", gap:12}}>
              <div>
                <label className="text-small" style={{display:"block", fontWeight:600, marginBottom:4}}>Sito web</label>
                <input
                  type="text"
                  className="input"
                  value={editModal.form.website}
                  onChange={e=>setEditModal({...editModal, form:{...editModal.form, website:e.target.value}})}
                  placeholder="esempio.it (senza https://)"
                  data-testid="edit-website"
                  style={{width:"100%"}}
                />
              </div>
              <div>
                <label className="text-small" style={{display:"block", fontWeight:600, marginBottom:4}}>Email primaria</label>
                <input
                  type="email"
                  className="input"
                  value={editModal.form.email}
                  onChange={e=>setEditModal({...editModal, form:{...editModal.form, email:e.target.value}})}
                  placeholder="info@esempio.it"
                  data-testid="edit-email"
                  style={{width:"100%"}}
                />
              </div>
              <div>
                <label className="text-small" style={{display:"block", fontWeight:600, marginBottom:4}}>Telefono</label>
                <input
                  type="text"
                  className="input"
                  value={editModal.form.phone}
                  onChange={e=>setEditModal({...editModal, form:{...editModal.form, phone:e.target.value}})}
                  placeholder="+39 02 1234567"
                  data-testid="edit-phone"
                  style={{width:"100%"}}
                />
              </div>
              <div>
                <label className="text-small" style={{display:"block", fontWeight:600, marginBottom:4}}>PEC</label>
                <input
                  type="email"
                  className="input"
                  value={editModal.form.pec}
                  onChange={e=>setEditModal({...editModal, form:{...editModal.form, pec:e.target.value}})}
                  placeholder="esempio@pec.it"
                  data-testid="edit-pec"
                  style={{width:"100%"}}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end mt-4">
              <button onClick={()=>setEditModal(null)} className="btn btn-secondary" disabled={busy} data-testid="edit-cancel">Annulla</button>
              <button onClick={saveEdit} className="btn btn-primary" disabled={busy} data-testid="edit-save">
                <Save size={14}/> Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {aiEmailModal && (
        <div className="modal-backdrop" onClick={()=>setAiEmailModal(null)} data-testid="ai-email-modal">
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth: 720, padding: 0, maxHeight: "90vh", display: "flex", flexDirection: "column"}}>
            <div style={{padding:"14px 18px",borderBottom:"1px solid #eee",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div className="flex items-center gap-2">
                <Wand2 size={18} color="#7c3aed"/>
                <div style={{fontWeight: 600, fontSize: 16}}>Email iper-personalizzata · Claude AI</div>
                {aiEmailModal.cached && (
                  <span style={{background:"#dcfce7",color:"#15803d",padding:"2px 8px",borderRadius:4,fontSize:11,fontWeight:600}}>
                    ♻ Cache (€0)
                  </span>
                )}
              </div>
              <button onClick={()=>setAiEmailModal(null)} className="btn btn-ghost btn-sm"><X size={16}/></button>
            </div>
            <div style={{flex: 1, overflowY: "auto", padding: 18}}>
              <div style={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", marginBottom: 14, fontSize: 13}}>
                <div className="text-muted">Modello:</div>
                <div><code>{aiEmailModal.model}</code></div>
                <div className="text-muted">Subject:</div>
                <div style={{fontWeight: 600}} data-testid="ai-subject">{aiEmailModal.subject}</div>
                <div className="text-muted">Preheader:</div>
                <div className="text-small text-muted">{aiEmailModal.preheader}</div>
                {aiEmailModal.warmthScore != null && (
                  <>
                    <div className="text-muted">Warmth score:</div>
                    <div>
                      {[...Array(10)].map((_, i) => (
                        <Star key={i} size={11} fill={i < aiEmailModal.warmthScore ? "#f59e0b" : "transparent"} color={i < aiEmailModal.warmthScore ? "#f59e0b" : "#cbd5e1"}/>
                      ))}
                      <span style={{marginLeft: 6, fontWeight: 600}}>{aiEmailModal.warmthScore}/10</span>
                    </div>
                  </>
                )}
              </div>

              {aiEmailModal.personalizationHooks?.length > 0 && (
                <div className="card mb-3" style={{padding: 12, background: "#faf5ff", borderLeft: "3px solid #7c3aed"}}>
                  <div className="text-small" style={{fontWeight: 600, color: "#5b21b6", marginBottom: 6}}>
                    🎯 Hook personalizzati estratti dall'analisi
                  </div>
                  <ul className="text-small" style={{marginLeft: 18, lineHeight: 1.7, marginBottom: 0}}>
                    {aiEmailModal.personalizationHooks.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="text-small text-muted mb-2" style={{fontWeight: 600, textTransform: "uppercase"}}>
                Anteprima body email
              </div>
              <div className="card" style={{padding: 16, background: "#fff", border: "1px solid #e5e7eb",
                lineHeight: 1.7, fontSize: 14}}>
                <div dangerouslySetInnerHTML={{__html: aiEmailModal.bodyHtml}}/>
              </div>
            </div>
            <div style={{padding: "12px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "space-between", gap: 8}}>
              <button onClick={()=>generateAiEmail(aiEmailModal.item, true)} className="btn btn-ghost"
                data-testid="ai-regenerate" disabled={loading}>
                <RefreshCw size={14}/> Rigenera (€0,01)
              </button>
              <div className="flex gap-2">
                <button onClick={()=>setAiEmailModal(null)} className="btn btn-ghost">Chiudi</button>
                <button onClick={async () => {
                  if (!window.confirm(`Inviare questa email personalizzata a ${aiEmailModal.item.company?.email}?`)) return;
                  setBusy(true);
                  try {
                    const { data } = await api.post(
                      `/lists/${id}/items/${aiEmailModal.item.id}/email/send`,
                      { subject: aiEmailModal.subject, body_html: aiEmailModal.bodyHtml }
                    );
                    if (data.success) {
                      alert("Email inviata!");
                      setAiEmailModal(null);
                      await reload();
                    } else alert("Errore: " + (data.error || "sconosciuto"));
                  } finally { setBusy(false); }
                }} className="btn btn-primary" data-testid="ai-send" disabled={busy}>
                  <Send size={14}/> Invia questa email
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
